import { useLayoutEffect, useMemo, useState } from 'react'
import type { Paragraph } from '@/api/types/stowed-element'
import { useShapeSpellActiveWord } from '@/components/app/shapes/shape-spell-active-word'
import { useShapeSpellUnderlines } from '@/components/app/shapes/shape-spell-underlines'
import { splitShapeTextParagraphsAtOffset } from '@/lib/shapes/fit-paragraphs-to-shape'
import { computeSelectionRects } from '@/lib/shapes/shape-text'
import type { ShapeData } from '@/lib/shapes/types/shape-data'
import { hasVisibleOverset } from '../helpers/visible-overset'
import { peekContentHeight } from '../helpers/peek-content-height'
import { useShapeTextLayout } from '../hooks/use-shape-text-layout'
import { DropCapGlyph } from './drop-cap-glyph'
import { ShapeTextChunk } from './shape-text-chunk'
import { SpellUnderlines } from './spell-underlines'
import { SpellWordAnchor } from './spell-word-anchor'

/** How many times taller than the frame the tail's rectangle may grow while it looks for the end. */
const MAX_PEEK_GROWTH = 32

interface ShapeTextPeekProps {
  shape: ShapeData
  paragraphs: Paragraph[]
  /** How many leading characters the frame itself draws; the peek shows what comes after. */
  consumedLength: number
  /**
   * Where the text the frame draws ends: the bottom of its last drawn row, or 0 when it drew none.
   * The hidden text carries on from here, one row after the last, at the same spacing.
   */
  drawnBottom: number
}

/**
 * The overflow peek (WMPCM-1212, SC-5): while the Spell Check popover is open on a misspelling in
 * hidden text, the text the frame cannot fit is shown below the frame's bottom edge, so the user can
 * see the word in its place.
 *
 * The frame keeps painting its own text exactly as it always does, in its own outline, so nothing
 * above the edge moves. This component paints only the hidden tail, laid out in a plain rectangle as
 * wide as the frame and carrying on from the last row the frame drew, so the revealed lines keep the
 * frame's own spacing instead of starting a row's worth lower at the frame's edge. Laying the tail out
 * in the frame's own
 * outline, even stretched taller, would be wrong for any frame that is not a rectangle: a circle or
 * triangle would change its line widths, and a freeform path keeps its fixed outline however tall
 * it is made, so no hidden text would ever fit.
 *
 * Paint only: the frame is never resized and nothing is saved. Nothing else is drawn: the selected
 * frame's own outline shows where the frame really ends, as in the design demo. The frame's own
 * layout and registered handle are untouched, because the spell scan reads what is hidden from them;
 * only its overflow badge steps aside while the peek is open (`ShapeTextEditor`).
 */
export function ShapeTextPeek({
  shape,
  paragraphs,
  consumedLength,
  drawnBottom,
}: ShapeTextPeekProps) {
  const [growth, setGrowth] = useState(1)

  const tailParagraphs = useMemo(
    () => splitShapeTextParagraphsAtOffset(paragraphs, consumedLength).overflow,
    [paragraphs, consumedLength]
  )
  // A plain rectangle the frame's width. The frame's own fields stay (stroke and the like set the
  // edge clearance), but its outline is replaced, so the tail flows the way text does in a box.
  const tailShape = useMemo<ShapeData>(
    () => ({
      ...shape,
      type: 'square',
      kindId: 1,
      path: undefined,
      pathCommands: undefined,
      x: 0,
      y: 0,
      rotation: 0,
      height: shape.height * growth,
    }),
    [shape, growth]
  )

  const {
    textValue,
    charRunMap,
    layout,
    capBoxes,
    fonts,
    pointSize,
    rowMetricPt,
    familyId,
    cssFamily,
    fontWeight,
    fontStyle,
  } = useShapeTextLayout({ shape: tailShape, paragraphs: tailParagraphs, isEditing: false })

  // The rectangle may be many times too short for the tail, so it doubles until the layout takes it
  // all. The layout effect runs before paint, so the steps in between are never seen.
  const isStillHidden = hasVisibleOverset(textValue, layout.consumedLength)
  const isSearching = isStillHidden && growth < MAX_PEEK_GROWTH

  useLayoutEffect(() => {
    if (isSearching) setGrowth((current) => current * 2)
  }, [isSearching, growth])

  const dropCapCharIndices = useMemo(
    () => new Set(capBoxes.map((box) => box.charIndex)),
    [capBoxes]
  )
  const tailHeight = peekContentHeight(layout.chunks, 0)
  // The tail's first row sits where the frame's next row would have gone. Its own layout starts a top
  // inset down, so the tail is lifted by that inset. A frame that drew no row starts it at the top.
  const firstRowTop = layout.chunks[0]?.yTop ?? 0
  const tailTop = drawnBottom > 0 ? Math.max(0, drawnBottom - firstRowTop) : 0
  const spellRanges = useShapeSpellUnderlines(shape.id)
  // The store holds ranges in the whole text's offsets; the tail's layout counts from its own start.
  const spellUnderlineRects = useMemo(
    () =>
      spellRanges
        .filter((range) => range.end > consumedLength)
        .flatMap((range) =>
          computeSelectionRects(
            layout.carets,
            Math.max(range.start, consumedLength) - consumedLength,
            Math.min(range.end - consumedLength, layout.consumedLength)
          )
        ),
    [spellRanges, consumedLength, layout.carets, layout.consumedLength]
  )

  // The word the Spell Check popover is open on, when it sits in this hidden tail: marked here, as
  // the frame marks a visible one, so the popover opens under it.
  const activeSpellWord = useShapeSpellActiveWord(shape.id)
  const activeSpellWordRect = useMemo(
    () =>
      activeSpellWord && activeSpellWord.start >= consumedLength
        ? (computeSelectionRects(
            layout.carets,
            activeSpellWord.start - consumedLength,
            Math.min(activeSpellWord.end - consumedLength, layout.consumedLength)
          )[0] ?? null)
        : null,
    [activeSpellWord, consumedLength, layout.carets, layout.consumedLength]
  )

  return (
    <div
      data-shape-text-peek="true"
      className="pointer-events-none absolute left-0 top-0"
      style={{ width: shape.width, height: Math.max(shape.height, tailTop + tailHeight) }}
    >
      <div className="absolute inset-x-0" style={{ top: tailTop, height: tailHeight }}>
        {/* The tail's own coordinates start at its top-left, `tailTop` down from the frame's top. */}
        <div className="absolute inset-0">
          {layout.chunks.map((chunk, chunkIdx) => (
            <ShapeTextChunk
              key={`shape-text-peek-chunk-${shape.id}-${chunkIdx}`}
              chunk={chunk}
              charRunMap={charRunMap}
              advances={layout.advances}
              dropCapCharIndices={dropCapCharIndices}
              fonts={fonts}
              familyId={familyId}
              cssFamily={cssFamily}
              fontWeight={fontWeight}
              fontStyle={fontStyle}
              pointSize={pointSize}
              rowMetricPt={rowMetricPt}
            />
          ))}
          {capBoxes.map((box) => (
            <DropCapGlyph
              key={`shape-text-peek-dropcap-${shape.id}-${box.charIndex}`}
              box={box}
              run={charRunMap[box.charIndex] ?? null}
              fonts={fonts}
              familyId={familyId}
              cssFamily={cssFamily}
              fontWeight={fontWeight}
              fontStyle={fontStyle}
            />
          ))}
          {spellUnderlineRects.length > 0 && <SpellUnderlines rects={spellUnderlineRects} />}
          {activeSpellWord && activeSpellWordRect && (
            <SpellWordAnchor
              rect={activeSpellWordRect}
              range={`${activeSpellWord.start}-${activeSpellWord.end}`}
            />
          )}
        </div>
      </div>
    </div>
  )
}
